/**
 * Operator-facing fleet roles for the execution dashboard.
 * Mirrors `src/agents/fleet-work-plan/fleet-plan-roles.ts` so the UI can
 * label cards even when a persisted plan predates `roleId`. Keep the
 * inference match table in lock-step with that module.
 */

export type FleetDisplayRole = {
  roleId: string;
  roleName: string;
  purpose: string;
};

export const FLEET_DISPLAY_ROLES: Record<string, { roleName: string; purpose: string }> = {
  planner: { roleName: "Planner", purpose: "Analyze & plan" },
  code_analyst: { roleName: "Code Analyst", purpose: "Read & understand code" },
  backend_developer: {
    roleName: "Backend Developer",
    purpose: "Implement backend services, APIs and core logic",
  },
  frontend_developer: { roleName: "Frontend Developer", purpose: "Update UI (if needed)" },
  test_engineer: { roleName: "Test Engineer", purpose: "Add & run tests" },
  documentation: { roleName: "Documentation", purpose: "Update docs" },
  reviewer: { roleName: "Reviewer", purpose: "Review changes" },
  summarizer: { roleName: "Summarizer", purpose: "Prepare final result" },
};

export const FLEET_DISPLAY_ROLE_ORDER: readonly string[] = [
  "planner",
  "code_analyst",
  "backend_developer",
  "frontend_developer",
  "test_engineer",
  "documentation",
  "reviewer",
  "summarizer",
];

export type FleetUnitRoleHint = {
  roleId?: string;
  roleName?: string;
  requiredSkills?: readonly string[];
  targetAgentId?: string;
  plannerAgentId?: string;
  title?: string;
  description?: string;
  phase?: string;
};

function haystack(hint: FleetUnitRoleHint): string {
  return [
    hint.phase,
    hint.title,
    hint.description,
    hint.targetAgentId,
    ...(hint.requiredSkills ?? []),
  ]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(" ")
    .toLowerCase();
}

function resolveRole(roleId: string, roleName?: string): FleetDisplayRole {
  const known = FLEET_DISPLAY_ROLES[roleId];
  if (known) {
    return { roleId, roleName: roleName?.trim() || known.roleName, purpose: known.purpose };
  }
  return {
    roleId,
    roleName: roleName?.trim() || roleId,
    purpose: roleName?.trim() || roleId,
  };
}

export function inferFleetDisplayRole(hint: FleetUnitRoleHint): FleetDisplayRole {
  if (hint.roleId?.trim()) {
    return resolveRole(hint.roleId.trim(), hint.roleName);
  }

  const text = haystack(hint);
  if (hint.phase === "extract" || /\b(extract|analy[sz]e|ir\b|research)\b/.test(text)) {
    return resolveRole("code_analyst");
  }
  if (/\btest/.test(text) || /\bqa\b/.test(text)) {
    return resolveRole("test_engineer");
  }
  if (/\b(doc|readme|guide)/.test(text)) {
    return resolveRole("documentation");
  }
  if (/\b(review|reviewer)\b/.test(text)) {
    return resolveRole("reviewer");
  }
  if (/\b(summar|final result)\b/.test(text)) {
    return resolveRole("summarizer");
  }
  if (/\b(frontend|ui|css|design)\b/.test(text)) {
    return resolveRole("frontend_developer");
  }
  if (/\b(implement|backend|coder|code-fix|api|server)\b/.test(text)) {
    return resolveRole("backend_developer");
  }
  if (!hint.targetAgentId || hint.targetAgentId === hint.plannerAgentId || /\bplan/.test(text)) {
    return resolveRole("planner");
  }
  return resolveRole("backend_developer");
}

export function roleOrderIndex(roleId: string): number {
  const index = FLEET_DISPLAY_ROLE_ORDER.indexOf(roleId);
  return index === -1 ? FLEET_DISPLAY_ROLE_ORDER.length : index;
}
